import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Scale, FileText, Users, Handshake, XCircle, Info, ArrowRight } from "lucide-react";
import api from "../lib/api";

const PRINCIPLES = [
  "Listen first.",
  "Validate with the community.",
  "Choose projects by evidence.",
  "Fund transparently.",
  "Implement with local ownership.",
  "Maintain with community responsibility.",
  "Report publicly.",
  "Repeat with improvement.",
];

const OPERATING_MODEL = [
  { who: "COMMUNITY", what: "Identifies and validates the need." },
  { who: "DUTSEN-KAUNA FOUNDATION", what: "Researches and designs the intervention." },
  { who: "SUPPORTER / FUNDER", what: "Provides agreed financial or material support." },
  { who: "DUTSEN-KAUNA FOUNDATION", what: "Procures, implements and monitors independently." },
  { who: "COMMUNITY", what: "Receives the intervention and participates in ownership." },
  { who: "DUTSEN-KAUNA FOUNDATION", what: "Reports expenditure, completion and impact." },
  { who: "SUPPORTER", what: "Receives transparent factual acknowledgement." },
];

const NEVER_ACCEPT = [
  "Join a political party",
  "Attend political rallies",
  "Campaign for a politician",
  "Vote for a particular candidate",
  "Display political loyalty",
  "Produce voter cards as a condition of charitable assistance",
  "Publicly endorse a politician",
  "Participate in campaign activities",
];

const RETAINS = [
  { t: "Research", d: "Understanding and verifying the community need." },
  { t: "Project design", d: "Determining the most appropriate intervention." },
  { t: "Budgeting", d: "Preparing realistic and transparent project costs." },
  { t: "Procurement", d: "Selecting suppliers and contractors per Foundation procedures." },
  { t: "Implementation", d: "Managing delivery." },
  { t: "Monitoring", d: "Checking quality, expenditure and progress." },
  { t: "Community engagement", d: "Keeping beneficiaries and community representatives involved." },
  { t: "Completion", d: "Verifying delivery." },
  { t: "Reporting", d: "Publishing appropriate financial and impact information." },
];

const STATUS_LABEL = {
  planned: "Planned",
  funded: "Funded",
  in_progress: "In progress",
  completed: "Completed",
};

export default function Governance() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    document.title = "Public Leadership & Politician-Supported Community Framework | Dutsen Kauna Foundation";
    api.get("/supported-projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="governance-page">
      {/* Hero */}
      <section className="relative bg-[#155e3f] text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] to-[#155e3f]" />
        <div className="dk-container relative">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Governance framework</span>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold mt-3 max-w-4xl leading-[1.1]">
            Public leadership & politician-supported community <span className="italic text-[#d6aa43]">project framework.</span>
          </h1>
          <p className="mt-6 text-white text-lg max-w-3xl">
            A practical framework for accepting community-development support while protecting independence,
            community trust, transparency and impact.
          </p>
        </div>
      </section>

      {/* Core principle */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <div className="max-w-4xl">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Core operating principle</span>
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              {[
                "The supporter provides funding or resources.",
                "The Foundation independently identifies or validates community needs.",
                "The Foundation designs, implements, monitors and reports the project.",
                "The community receives the benefit.",
                "The supporter receives factual acknowledgement — not political endorsement.",
              ].map((t, i) => (
                <div key={t} className="p-5 rounded-xl border border-[#d6aa43]/20 bg-[#f8f4e8] flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#d6aa43] text-[#0a0d12] flex items-center justify-center font-bold text-sm flex-shrink-0">{i + 1}</div>
                  <p className="text-[#0a0d12]/85">{t}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our position */}
      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <Scale className="text-[#d6aa43] mb-3" />
            <h2 className="font-serif text-3xl text-[#0a0d12] font-bold">Our position</h2>
          </div>
          <div className="lg:col-span-2 space-y-4 text-[#0a0d12]/80">
            <p>Dutsen Kauna Foundation welcomes legitimate support for community development from elected representatives, political officeholders, political aspirants, government officials, public institutions, private individuals, businesses, development partners and philanthropists.</p>
            <div className="p-5 rounded-md bg-[#155e3f] text-white">
              <p className="font-serif text-lg">
                Accepting support <strong className="text-[#d6aa43]">does not</strong> mean the Foundation endorses any political party, candidate, campaign or ideology.
              </p>
              <p className="mt-2 text-white text-sm">The Foundation's responsibility remains to the communities and people it serves.</p>
            </div>
          </div>
        </div>
      </section>

      {/* No political conditions */}
      <section className="py-16 bg-white">
        <div className="dk-container">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 rounded-xl border-l-4 border-red-500 bg-red-50">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="text-red-600" />
                <h3 className="font-serif text-xl text-[#0a0d12] font-bold">No political conditions</h3>
              </div>
              <p className="text-sm text-[#0a0d12]/80 mb-4">Support requiring beneficiaries to do any of the following will be declined:</p>
              <ul className="space-y-1.5 text-sm text-[#0a0d12]/80">
                {NEVER_ACCEPT.map((t) => (
                  <li key={t} className="flex gap-2"><XCircle size={14} className="text-red-500 mt-1 flex-shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="p-6 rounded-xl border-l-4 border-[#d6aa43] bg-[#f8f4e8]">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="text-[#d6aa43]" />
                <h3 className="font-serif text-xl text-[#0a0d12] font-bold">No discrimination by affiliation</h3>
              </div>
              <p className="text-sm text-[#0a0d12]/80">
                Beneficiaries are never selected because they support or oppose a particular politician.
                No person is excluded from Foundation assistance because of their political views or associations.
                Selection follows the published criteria of the relevant programme.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Foundation retains */}
      <section className="py-20 bg-[#155e3f] text-white">
        <div className="dk-container">
          <div className="max-w-3xl mb-10">
            <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Foundation retains independent control</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3">Where a supporter funds a project, the Foundation runs it.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RETAINS.map((r, i) => (
              <div key={r.t} className="p-5 rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`retains-${i}`}>
                <div className="text-[#d6aa43] text-xs font-bold tracking-[0.2em] uppercase">0{i + 1}</div>
                <h4 className="font-serif text-lg font-bold mt-1 mb-1">{r.t}</h4>
                <p className="text-sm text-white">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating model diagram */}
      <section className="py-20 bg-[#f8f4e8]">
        <div className="dk-container max-w-3xl">
          <div className="mb-10">
            <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Simple operating model</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mt-3">Support the community without buying the community.</h2>
          </div>
          <div className="space-y-3">
            {OPERATING_MODEL.map((step, i) => (
              <div key={`${step.who}-${step.what}`} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#d6aa43] text-[#0a0d12] flex items-center justify-center font-serif font-bold flex-shrink-0">{i + 1}</div>
                <div className="flex-1 p-4 rounded-lg bg-white border border-black/5">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#d6aa43] font-bold">{step.who}</div>
                  <p className="text-[#0a0d12]/85 mt-1">{step.what}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Golden rule */}
      <section className="py-16 bg-[#155e3f] text-white">
        <div className="dk-container max-w-3xl text-center">
          <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">The golden rule</span>
          <h2 className="font-serif text-4xl md:text-6xl font-bold mt-4 leading-tight">
            Support the community <span className="italic text-[#d6aa43]">without buying</span> the community.
          </h2>
          <p className="mt-6 text-white">
            No financial contribution should purchase votes, political loyalty, endorsement or control.
            It should purchase only one thing:
          </p>
          <p className="mt-3 font-serif text-2xl text-[#d6aa43]">Greater capacity to serve people and communities.</p>
        </div>
      </section>

      {/* Public Transparency Record */}
      <section className="py-20 bg-white" data-testid="transparency-record">
        <div className="dk-container">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div className="max-w-2xl">
              <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Public transparency record</span>
              <h2 className="font-serif text-3xl md:text-5xl text-[#0a0d12] font-bold mt-3">Every supported project — publicly documented.</h2>
            </div>
            <Link to="/donate" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Fund a project <ArrowRight size={14} /></Link>
          </div>

          {projects.length === 0 ? (
            <div className="p-8 rounded-2xl border border-dashed border-[#d6aa43]/30 bg-[#f8f4e8]/60 text-center text-[#0a0d12]/70">
              No supported projects are on record yet. As projects are funded and completed, they will be listed here with full transparency information.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {projects.map((p) => (
                <div key={p.id} className="p-6 rounded-xl border border-black/5 bg-[#f8f4e8]" data-testid={`sp-${p.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      {p.serial && <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#d6aa43] font-bold mb-1">{p.serial}</div>}
                      <h3 className="font-serif text-xl text-[#0a0d12] font-bold">{p.project_name}</h3>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-bold ${p.status === "completed" ? "bg-green-100 text-green-800" : p.status === "in_progress" ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"}`}>{STATUS_LABEL[p.status] || p.status}</span>
                  </div>
                  <div className="text-xs text-[#0a0d12]/60 mb-3">{p.location}</div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <KV k="Need identified" v={p.need_identified_through} />
                    <KV k="Supported by" v={`${p.supporter_name}${p.supporter_role ? " · " + p.supporter_role : ""}`} />
                    <KV k="Amount / value" v={p.amount_value || (p.amount_numeric ? `${p.currency} ${Number(p.amount_numeric).toLocaleString()}` : "—")} />
                    <KV k="Implemented by" v={p.implemented_by} />
                    <KV k="Beneficiaries" v={p.beneficiaries} />
                  </div>
                  {p.acknowledgement_text && (
                    <p className="text-xs italic text-[#0d1724]/75 mt-2 border-l-2 border-[#d6aa43] pl-3">{p.acknowledgement_text}</p>
                  )}
                  <div className="flex gap-3 mt-3 text-xs">
                    {p.completion_report_url && <a href={p.completion_report_url} target="_blank" rel="noreferrer" className="text-[#d6aa43] font-semibold hover:underline">Completion report</a>}
                    {p.impact_report_url && <a href={p.impact_report_url} target="_blank" rel="noreferrer" className="text-[#d6aa43] font-semibold hover:underline">Impact report</a>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Community principles */}
      <section className="py-16 bg-[#f8f4e8]">
        <div className="dk-container max-w-3xl">
          <span className="text-[#0d1724] tracking-[0.3em] text-xs font-semibold uppercase">Our community development principle</span>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {PRINCIPLES.map((t) => (
              <div key={t} className="p-4 rounded-md bg-white border border-black/5 flex gap-2 items-start">
                <CheckCircle2 size={16} className="text-[#d6aa43] flex-shrink-0 mt-1" />
                <span className="text-sm font-semibold text-[#0a0d12]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Independence notice */}
      <section className="py-16 bg-white">
        <div className="dk-container max-w-3xl">
          <div className="p-8 rounded-2xl border-l-4 border-[#d6aa43] bg-[#155e3f] text-white">
            <div className="flex items-center gap-2 mb-3">
              <Info className="text-[#d6aa43]" size={20} />
              <span className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] font-semibold">Foundation independence statement</span>
            </div>
            <p className="text-white text-sm leading-relaxed">
              Dutsen Kauna Foundation is an independent community-development and humanitarian organisation.
              Acceptance of financial, material or technical support from an elected official, politician,
              candidate, government institution, business, individual or organisation does not constitute
              political endorsement. The Foundation independently determines its research methodology,
              beneficiary criteria, project assessment, implementation, monitoring and reporting procedures.
              Supporters may receive appropriate factual acknowledgement for their contribution.
              They do not purchase political endorsement, beneficiary loyalty or control of Foundation decisions.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

const KV = ({ k, v }) => (<div><div className="text-[10px] uppercase tracking-wide text-[#0a0d12]/60">{k}</div><div className="font-semibold text-[#0a0d12]">{v || "—"}</div></div>);
